/**
 * CallLogsSettingsDrawer — "Report Settings" drawer for the Call Logs report.
 *
 * Mirrors the look and behavior of alert-details' ReportFilterDrawer (same
 * dp-drawer / ar-accordion / ar-time-bar / ar-custom-range classes) but is
 * scoped to just the two things this report needs:
 *   1. Time Range (presets + custom date range, date-only)
 *   2. Call Types (search + checkbox multi-select)
 *
 * Time-range changes take effect immediately on the parent (live), matching
 * AlertDetailsPage. Call-type selections are held in a working copy and
 * committed via "Apply Settings".
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const CallLogsSettingsDrawer = ({
    open,
    onClose,
    onApply,
    onClear,
    saving,
    // Call types
    callTypes,            // [{ call_type_id, call_type_name }]
    selectedCallTypes,    // number[] (current applied selection)
    // Time range
    timeRanges,
    rangeId,
    customRange,
    start,
    end,
    onTimeRangeChange,
    onCustomToggle,
    onStartChange,
    onEndChange,
}) => {
    const [expanded, setExpanded] = useState(new Set(['timeRange', 'callTypes']));
    const [working, setWorking] = useState(() => new Set((selectedCallTypes || []).map(String)));
    const [search, setSearch] = useState('');
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);

    // Sync working copy whenever the drawer opens
    useEffect(() => {
        if (open) {
            setWorking(new Set((selectedCallTypes || []).map(String)));
            setSearch('');
            setShowSelectedOnly(false);
        }
    }, [open, selectedCallTypes]);

    // Auto-focus the custom start picker when opened already in Custom mode
    const customStartRef = useRef(null);
    const customGuardRef = useRef(false);
    useEffect(() => {
        const shouldFocus = open && customRange;
        if (shouldFocus && !customGuardRef.current) {
            customGuardRef.current = true;
            setExpanded((s) => (s.has('timeRange') ? s : new Set([...s, 'timeRange'])));
            const t = setTimeout(() => { try { customStartRef.current?.setFocus?.(); } catch { /* no-op */ } }, 80);
            return () => clearTimeout(t);
        }
        if (!shouldFocus) customGuardRef.current = false;
        return undefined;
    }, [open, customRange]);

    const toggleSection = useCallback((id) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    const toggleType = useCallback((id) => {
        setWorking((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    const selectAll = useCallback(() => {
        setWorking(new Set((callTypes || []).map((c) => String(c.call_type_id))));
    }, [callTypes]);

    const clearTypes = useCallback(() => setWorking(new Set()), []);

    const filteredTypes = useMemo(() => {
        let list = callTypes || [];
        if (showSelectedOnly) list = list.filter((c) => working.has(String(c.call_type_id)));
        if (search) {
            const term = search.toLowerCase();
            list = list.filter((c) => (c.call_type_name || '').toLowerCase().includes(term));
        }
        return list;
    }, [callTypes, search, showSelectedOnly, working]);

    const handleApply = useCallback(() => {
        // Emit numeric ids (call_type_id is an int)
        onApply([...working].map((id) => parseInt(id, 10)));
    }, [working, onApply]);

    const handleClearAll = useCallback(() => {
        setWorking(new Set());
        onClear();
    }, [onClear]);

    const timeLabel = customRange
        ? 'Custom'
        : (timeRanges || []).find((r) => r.id === rangeId)?.label || 'Yesterday';

    return (
        <>
            {open ? <div className="dp-drawer-backdrop" onClick={onClose} /> : null}
            <div className={`dp-drawer dp-drawer--lg${open ? ' is-open' : ''}`}>
                <div className="dp-drawer__header">
                    <h2 className="dp-drawer__title">Report Settings</h2>
                    <button className="dp-drawer__close" onClick={onClose}>{'✕'}</button>
                </div>

                <div className="dp-drawer__body" style={{ overflowY: 'auto', flex: 1 }}>
                    {/* Time Range accordion */}
                    <div className="ar-accordion">
                        <button
                            className={`ar-accordion__header${expanded.has('timeRange') ? ' ar-accordion__header--open' : ''}`}
                            onClick={() => toggleSection('timeRange')}
                            type="button"
                        >
                            <span className="ar-accordion__icon">{expanded.has('timeRange') ? '▼' : '▶'}</span>
                            <span className="ar-accordion__label">Time Range</span>
                            <span className="ar-accordion__count">{timeLabel}</span>
                        </button>
                        {expanded.has('timeRange') ? (
                            <div className="ar-accordion__body">
                                <div className="ar-time-bar">
                                    <div className="ar-time-bar__ranges">
                                        {(timeRanges || []).map((range) => (
                                            <button
                                                key={range.id}
                                                className={`ar-time-btn${rangeId === range.id && !customRange ? ' ar-time-btn--active' : ''}`}
                                                onClick={() => onTimeRangeChange(range)}
                                                type="button"
                                            >
                                                {range.label}
                                            </button>
                                        ))}
                                        <button
                                            className={`ar-time-btn${customRange ? ' ar-time-btn--active' : ''}`}
                                            onClick={onCustomToggle}
                                            type="button"
                                        >
                                            Custom
                                        </button>
                                    </div>
                                    {customRange ? (
                                        <div className="ar-custom-range">
                                            <DatePicker
                                                ref={customStartRef}
                                                selected={start}
                                                onChange={(date) => onStartChange(date)}
                                                selectsStart
                                                startDate={start}
                                                endDate={end}
                                                maxDate={end}
                                                dateFormat="MM/dd/yyyy"
                                                className="ar-custom-range__input"
                                                placeholderText="Start date"
                                            />
                                            <span className="ar-custom-range__sep">to</span>
                                            <DatePicker
                                                selected={end}
                                                onChange={(date) => onEndChange(date)}
                                                selectsEnd
                                                startDate={start}
                                                endDate={end}
                                                minDate={start}
                                                dateFormat="MM/dd/yyyy"
                                                className="ar-custom-range__input"
                                                placeholderText="End date"
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {/* Call Types accordion */}
                    <div className="ar-accordion">
                        <button
                            className={`ar-accordion__header${expanded.has('callTypes') ? ' ar-accordion__header--open' : ''}`}
                            onClick={() => toggleSection('callTypes')}
                            type="button"
                        >
                            <span className="ar-accordion__icon">{expanded.has('callTypes') ? '▼' : '▶'}</span>
                            <span className="ar-accordion__label">Call Types</span>
                            {working.size > 0 ? (
                                <span className="ar-accordion__count">{working.size} selected</span>
                            ) : null}
                        </button>
                        {expanded.has('callTypes') ? (
                            <div className="ar-accordion__body">
                                <div className="ar-filter-section">
                                    <div className="ar-filter-section__header">
                                        <span className="ar-filter-section__label">Call Types</span>
                                        <div className="ar-filter-section__actions">
                                            <button className="ar-filter-section__btn" onClick={selectAll} type="button">Select All</button>
                                            <button className="ar-filter-section__btn" onClick={clearTypes} type="button">Clear</button>
                                            <button
                                                className={`ar-filter-section__btn${showSelectedOnly ? ' ar-filter-section__btn--active' : ''}`}
                                                onClick={() => setShowSelectedOnly((p) => !p)}
                                                type="button"
                                            >
                                                Show Selected
                                            </button>
                                        </div>
                                    </div>
                                    <input
                                        className="ar-filter-section__search"
                                        type="text"
                                        placeholder="Search call types..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                    <div className="ar-filter-list">
                                        {filteredTypes.length === 0 ? (
                                            <p className="ar-filter-list__empty">
                                                {showSelectedOnly ? 'No selections.' : 'No call types found.'}
                                            </p>
                                        ) : null}
                                        {filteredTypes.map((c) => {
                                            const id = String(c.call_type_id);
                                            return (
                                                <label key={id} className="ar-filter-list__item">
                                                    <input
                                                        type="checkbox"
                                                        checked={working.has(id)}
                                                        onChange={() => toggleType(id)}
                                                    />
                                                    <span>{c.call_type_name || '(unnamed)'}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>

                <div className="dp-drawer__footer">
                    <button className="dp-btn dp-btn--cancel" onClick={handleClearAll} disabled={saving} type="button">
                        Clear All
                    </button>
                    <button className="dp-btn dp-btn--primary" onClick={handleApply} disabled={saving} type="button">
                        {saving ? 'Applying…' : 'Apply Settings'}
                    </button>
                </div>
            </div>
        </>
    );
};

export default CallLogsSettingsDrawer;
